import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit,
  Output, ViewChild, ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { AuthRepository, User, UserRole } from '@app/core/auth/auth.repository';
import { OrientationService } from '@app/@shared/services/orientation.service';
import { PresenceService } from '@app/services/presence.service';
import { ProfileImageUrlPipe } from 'app/@shared/pipe/profile-image-url.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, MatIconModule, TranslateModule, ProfileImageUrlPipe],
})
export class UserComponent implements OnInit, OnDestroy {
  /* eslint-disable @typescript-eslint/naming-convention */
  static ngAcceptInputType_showAvatar: BooleanInput;
  /* eslint-enable @typescript-eslint/naming-convention */

  @Input() showAvatar: boolean = true;
  @Output() openHelpRequested = new EventEmitter<void>();
  @ViewChild('menuContainer') menuContainer!: ElementRef;

  user: User;
  _role: UserRole = UserRole.Student;
  _roleName: string = 'Student';
  UserRole = UserRole;
  isMenuOpen = false;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
    private authRepository: AuthRepository,
    private orientationService: OrientationService,
    private presenceService: PresenceService,
    private translate: TranslateService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.authRepository.$user.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
      if (!user) return;
      this.user = user;
      this._role = user.role || UserRole.Student;
      this._roleName = UserRole[this._role];
      this._changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Menu open / close
  // -----------------------------------------------------------------------------------------------------

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isMenuOpen = !this.isMenuOpen;
    this._changeDetectorRef.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isMenuOpen && this.menuContainer &&
        !this.menuContainer.nativeElement.contains(event.target)) {
      this.isMenuOpen = false;
      this._changeDetectorRef.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
      this._changeDetectorRef.markForCheck();
    }
  }

  private _closeMenu(): void {
    this.isMenuOpen = false;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Navigation helpers — all close the panel before navigating
  // -----------------------------------------------------------------------------------------------------

  navigateTo(route: string): void {
    this._closeMenu();
    this._router.navigate([route]);
  }

  navigateToTab(path: string, tab: string): void {
    this._closeMenu();
    this._router.navigate([path], { queryParams: { tab } });
  }

  openHelp(): void {
    this._closeMenu();
    this.openHelpRequested.emit();
  }

  startOrientation(): void {
    this._closeMenu();
    this.orientationService.startTourFromMenu();
  }

  signOut(): void {
    this._closeMenu();
    this.presenceService.stopPresence(true);
    this.authRepository.logout();
    this._router.navigate(['/sign-out']);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Badges
  // -----------------------------------------------------------------------------------------------------

  /** Role + company badges shown in the dropdown header */
  get roleBadges(): { label: string; cls: string }[] {
    const badges: { label: string; cls: string }[] = [];

    const translateRole = (name: string): string => {
      const key = `role.${name.toLowerCase()}`;
      const t = this.translate.instant(key);
      return t !== key ? t : name;
    };

    const roles = this.user?.roles;
    if (roles?.length) {
      roles.forEach(r => badges.push({ label: translateRole(r), cls: this._roleBadgeCls(r) }));
    } else {
      badges.push({ label: translateRole(this._roleName), cls: this._roleBadgeCls(this._roleName) });
    }

    if (this.user?.company) {
      badges.push({
        label: this.user.company,
        cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
      });
    }

    return badges;
  }

  private _roleBadgeCls(name: string): string {
    const n = (name || '').toLowerCase();
    if (n.includes('super') || n.includes('sysadmin'))
      return 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300';
    if (n.includes('courseadmin') || n.includes('course'))
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300';
    if (n.includes('supervisor'))
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
    if (n.includes('translator'))
      return 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300';
    if (n.includes('student'))
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }

  /** Whether to show the "Company Settings" item */
  get showCompanySettings(): boolean {
    const u = this.user;
    if (!u) return false;
    if (u.role === UserRole.CourseAdmin) return true;
    if (u.role === UserRole.Supervisor) {
      return u.permissions?.includes('mod_company_settings') ?? false;
    }
    return false;
  }
}
